import { createBigDecimal, divide, plus, times } from '@0xtorch/big-decimal'
import type { Transfer } from '@0xtorch/core'
import type { CostBasis } from '../../types/costBasis'
import type {
  ReplaceSubCategory,
  TransactionTransfer,
} from '../../types/transaction'
import { createAssetKey } from '../../utils'
import type {
  CreateCategoryTransactionParameters,
  CreateCategoryTransactionReturnTypes,
} from './types'
import { addBalanceToAssetBorrowsAndAssetBalances } from './util/addBalanceToAssetBorrowsAndAssetBalances'
import { getCostByAssetBalancesAndBorrows } from './util/getCostByAssetBalancesAndBorrows'

type CreateReplaceTransactionParameters =
  CreateCategoryTransactionParameters & {
    readonly subCategory?: ReplaceSubCategory
    readonly inTransfers: readonly Transfer[]
    readonly outTransfers: readonly Transfer[]
    readonly costBasis: CostBasis
  }

type CreateReplaceTransactionReturnTypes = CreateCategoryTransactionReturnTypes

export const createReplaceTransaction = ({
  actionId,
  timestamp,
  subCategory,
  inTransfers,
  outTransfers,
  assetBalances,
  assetBorrows,
  usedBalances,
  costBasis,
}: CreateReplaceTransactionParameters): CreateReplaceTransactionReturnTypes => {
  // handle out transfers
  let totalCost = createBigDecimal(0n)
  const outTransactionTransfers: TransactionTransfer[] = []
  for (const transfer of outTransfers) {
    const key = createAssetKey(transfer.asset)
    const price = transfer.price ?? createBigDecimal(0n)

    // Get cost by asset balance & asset borrow
    const {
      cost,
      assetBalances: keyAssetBalances,
      assetBorrows: keyAssetBorrows,
      usedBalance,
    } = transfer.asset.type === 'FiatCurrency'
      ? {
          cost: times(transfer.amount, price, 6),
          assetBalances: [],
          assetBorrows: [],
          usedBalance: {
            amount: createBigDecimal(0n),
            value: createBigDecimal(0n),
          },
        }
      : getCostByAssetBalancesAndBorrows({
          amount: transfer.amount,
          assetBalances: assetBalances.get(key) ?? [],
          assetBorrows: assetBorrows.get(key) ?? [],
          usedBalance: usedBalances.get(key) ?? {
            amount: createBigDecimal(0n),
            value: createBigDecimal(0n),
          },
          costBasis,
        })
    if (keyAssetBalances.length > 0) {
      assetBalances.set(key, keyAssetBalances)
    } else {
      assetBalances.delete(key)
    }
    if (keyAssetBorrows.length > 0) {
      assetBorrows.set(key, keyAssetBorrows)
    } else {
      assetBorrows.delete(key)
    }
    if (usedBalance.amount.value !== 0n) {
      usedBalances.set(key, usedBalance)
    } else {
      usedBalances.delete(key)
    }

    const balance = (assetBalances.get(key) ?? []).reduce(
      (acc, assetBalance) => plus(acc, assetBalance.amount),
      createBigDecimal(0n),
    )
    const borrowing = (assetBorrows.get(key) ?? []).reduce(
      (acc, assetBorrow) => plus(acc, assetBorrow.amount),
      createBigDecimal(0n),
    )

    outTransactionTransfers.push({
      amount: transfer.amount,
      asset: transfer.asset,
      price,
      zeroCompleted: transfer.price === undefined,
      cost,
      balance,
      borrowing,
      feePl: createBigDecimal(0n),
      incomePl: createBigDecimal(0n),
      tradePl: createBigDecimal(0n),
      totalPl: createBigDecimal(0n),
    })
    totalCost = plus(totalCost, cost)
  }

  // handle in transfers
  const inTransactionTransfers: TransactionTransfer[] = []
  const costPerInTransfer = divide(
    totalCost,
    createBigDecimal(BigInt(inTransfers.length)),
    6,
  )
  for (const transfer of inTransfers) {
    const key = createAssetKey(transfer.asset)
    const price = divide(
      times(costPerInTransfer, createBigDecimal(1n), 18),
      transfer.amount,
      18,
    )

    // Add balance
    const { assetBalances: keyAssetBalances, assetBorrows: keyAssetBorrows } =
      transfer.asset.type === 'FiatCurrency'
        ? {
            assetBalances: [],
            assetBorrows: [],
          }
        : addBalanceToAssetBorrowsAndAssetBalances({
            amount: transfer.amount,
            value: costPerInTransfer,
            assetBalances: assetBalances.get(key) ?? [],
            assetBorrows: assetBorrows.get(key) ?? [],
            costBasis,
          })
    if (keyAssetBalances.length > 0) {
      assetBalances.set(key, keyAssetBalances)
    } else {
      assetBalances.delete(key)
    }
    if (keyAssetBorrows.length > 0) {
      assetBorrows.set(key, keyAssetBorrows)
    } else {
      assetBorrows.delete(key)
    }

    const balance = (assetBalances.get(key) ?? []).reduce(
      (acc, assetBalance) => plus(acc, assetBalance.amount),
      createBigDecimal(0n),
    )
    const borrowing = (assetBorrows.get(key) ?? []).reduce(
      (acc, assetBorrow) => plus(acc, assetBorrow.amount),
      createBigDecimal(0n),
    )

    inTransactionTransfers.push({
      amount: transfer.amount,
      asset: transfer.asset,
      price,
      zeroCompleted: false,
      cost: costPerInTransfer,
      balance,
      borrowing,
      feePl: createBigDecimal(0n),
      incomePl: createBigDecimal(0n),
      tradePl: createBigDecimal(0n),
      totalPl: createBigDecimal(0n),
    })
  }

  return {
    transaction: {
      actionId,
      timestamp,
      order: 0,
      category: 'replace',
      subCategory,
      ins: inTransactionTransfers,
      outs: outTransactionTransfers,
    },
    assetBalances,
    assetBorrows,
    usedBalances,
  }
}
